/**
 * Importes de un concepto de CFDI, con soporte de gross-up.
 *
 * Contrato: es la ÚNICA fuente de verdad del cálculo. El wizard legacy y el
 * flujo rediseñado importan de aquí; si el número que ve el usuario en la
 * captura difiere del que se manda a timbrar, es un bug de esta función y de
 * ninguna otra.
 */

export interface ConceptRetentionRates {
  /** ISR retenido, en decimal (0.10 = 10%). */
  isrRate: number;
  /** IVA retenido, en decimal (0.106667 = 2/3 del 16%). */
  ivaRate: number;
}

export interface ConceptAmountsInput {
  unitPrice: number;
  quantity: number;
  discount: number;
  /** Tasa de IVA en decimal. `exento` y `no objeto` entran como 0. */
  taxRate: number;
  /**
   * Cuando es `true`, el precio capturado es lo que el emisor quiere RECIBIR,
   * no la base de la factura: el cálculo corre hacia atrás.
   */
  grossUp: boolean;
  retentions?: ConceptRetentionRates;
}

export interface ConceptTotals {
  subtotal: number;
  taxes: number;
  total: number;
}

export type GrossUpInput = Pick<ConceptAmountsInput, 'taxRate' | 'grossUp' | 'retentions'>;

/**
 * Con `grossUp` y retenciones, el monto capturado es lo que le queda al emisor
 * DESPUÉS de que el receptor retiene. Por eso el divisor descuenta las tasas
 * retenidas y no solo suma el IVA trasladado: usar `monto / (1 + iva)` cuando
 * hay retenciones subestima la base y el emisor recibe de menos.
 *
 * Sin gross-up el divisor es 1, así que el cálculo directo y el inverso son la
 * misma fórmula para los dos casos.
 */
const grossUpDivisor = ({ taxRate, grossUp, retentions }: GrossUpInput): number => {
  if (!grossUp || taxRate <= 0) return 1;

  const isrRate = retentions?.isrRate ?? 0;
  const ivaRetRate = retentions?.ivaRate ?? 0;
  const hasRetentions = isrRate > 0 || ivaRetRate > 0;

  return hasRetentions ? 1 + taxRate - isrRate - ivaRetRate : 1 + taxRate;
};

export const calculateConceptTotals = (input: ConceptAmountsInput): ConceptTotals => {
  const capturedAmount = input.unitPrice * input.quantity - input.discount;
  const subtotal = capturedAmount / grossUpDivisor(input);
  const taxes = subtotal * input.taxRate;

  return { subtotal, taxes, total: subtotal + taxes };
};

/**
 * Inverso exacto de `calculateConceptTotals`: qué precio unitario hay que
 * capturar para que el concepto quede con ESE subtotal.
 *
 * Lo necesita la edición in-place del paso 3, donde el usuario escribe sobre el
 * precio que ve (el neto que va al SAT) y no sobre el monto capturado. Vive
 * junto al cálculo directo y comparte su divisor a propósito: dos fórmulas
 * separadas divergen en cuanto alguien toque las retenciones.
 */
export const unitPriceForSubtotal = ({
  subtotal,
  quantity,
  discount,
  ...grossUp
}: GrossUpInput & { subtotal: number; quantity: number; discount: number }): number => {
  const capturedAmount = subtotal * grossUpDivisor(grossUp);
  return (capturedAmount + discount) / Math.max(quantity, 1);
};

/** Lo que el emisor recibe una vez que el receptor aplica las retenciones. */
export const calculateNetToReceive = (
  totals: ConceptTotals,
  retentions?: ConceptRetentionRates,
): number => {
  const isr = totals.subtotal * (retentions?.isrRate ?? 0);
  const ivaRet = totals.subtotal * (retentions?.ivaRate ?? 0);
  return totals.total - isr - ivaRet;
};

export const sumConceptTotals = (totals: ConceptTotals[]): ConceptTotals =>
  totals.reduce<ConceptTotals>(
    (acc, t) => ({
      subtotal: acc.subtotal + t.subtotal,
      taxes: acc.taxes + t.taxes,
      total: acc.total + t.total,
    }),
    { subtotal: 0, taxes: 0, total: 0 },
  );
